import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

import styles from "./SelectDropdown.module.css";
import classNames from "classnames";
import ChevronDown from "./Icons/Chevrons.svg";
import { useTranslation } from "react-i18next";

/**
 * Interactive component, Custom Select to filter the year and months for receipt list.
 */
export function SelectDropdown({
  options,
  onChange,
  placeholder,
  disabled,
  dropdownRight = false,
  className,
  isScrollable,
  visibleOptionsCount,
  makeThin = false,
  fromReport = false,
}) {
  const { t } = useTranslation();
  const [dropdownDisplay, changeDropDownDisplay] = useState(false);
  const openedDisplayValue = options?.[0] ?? placeholder;
  const openedOptions = dropdownDisplay ? options.slice(1) : options;
  const isFirstOptionSelected =
    String(placeholder) === String(openedDisplayValue) ||
    String(placeholder) === String(t(openedDisplayValue));

  const wrapperRef = useRef(null);
  const dropdownStyle =
    isScrollable && visibleOptionsCount
      ? { "--dropdown-visible-options": visibleOptionsCount }
      : undefined;
  useOutsideAlerter(wrapperRef);

  function useOutsideAlerter(ref) {
    useEffect(() => {
      function handleClickOutside(event) {
        if (ref.current && !ref.current.contains(event.target)) {
          changeDropDownDisplay(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }

  return (
    <div ref={wrapperRef} className={styles.container}>
      <button
        className={classNames(
          styles.selectDropdown,
          className,
          dropdownDisplay ? styles.selectDropdownOpened : styles.selectDropdown,
          fromReport ? styles.fromReportContainer : ""
        )}
        onClick={() => {
          if (dropdownDisplay) {
            if (!isFirstOptionSelected) {
              onChange(openedDisplayValue);
            }
            changeDropDownDisplay(false);
            return;
          }

          changeDropDownDisplay(true);
        }}
        disabled={disabled}
      >
        <span
          className={classNames(
            makeThin ? styles.placeholderThin : styles.placeholder,
            dropdownDisplay &&
              !isFirstOptionSelected &&
              styles.placeholderOpened
          )}
        >
          {t(dropdownDisplay ? openedDisplayValue : placeholder)}
        </span>
        <span className={styles.selectChevron}>
          <img src={ChevronDown} />
        </span>
      </button>
      {dropdownDisplay && (
        <div
          className={classNames(
            dropdownRight ? styles.selectOptionsRight : styles.selectOptions,
            { [styles.scrollable]: isScrollable },
            { [styles.fromReport]: fromReport }
          )}
          style={dropdownStyle}
        >
          {openedOptions.map((option) => (
            <button
              className={classNames(
                styles.selectMenuOptions,
                String(option) === String(placeholder) &&
                  styles.selectedMenuOption
              )}
              key={option}
              onClick={() => {
                onChange(option);
                changeDropDownDisplay(false);
              }}
            >
              {t(option)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

SelectDropdown.propTypes = {
  options: PropTypes.array,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  visibleOptionsCount: PropTypes.number,
};
